import React, { useCallback } from 'react';

import type { QueryResult } from '@apollo/client';
import type { UpdateQueryFn } from '@apollo/client/core/watchQueryOptions';
import produce from 'immer';

import type { SubscriptionDescriptor } from './ReactiveSource';

type UnsubscribeHandler = () => void;

const DEFAULT_CREATE_SUBSCRIPTION_NAME = 'create';

export type UseCreateSubscriptionsInput<EntityResponse, QueryResponse> = {
  subscriptions?: SubscriptionDescriptor[];
  queryResult: QueryResult<QueryResponse>;
  selectEntityId: (entity: EntityResponse) => string;
  selectEntities: (resp?: QueryResponse) => EntityResponse[];
};

export default function useCreateSubscriptions<EntityResponse, QueryResponse>({
  subscriptions,
  queryResult,
  selectEntities,
  selectEntityId,
}: UseCreateSubscriptionsInput<EntityResponse, QueryResponse>) {
  const [createUnSubs, setCreateUnSubs] = React.useState<UnsubscribeHandler[]>();

  const subscribe = useCallback(() => {
    if (!createUnSubs && subscriptions) {
      // Subscribe to each descriptor using the `subscribeToMore` function and store
      // the unsubscribe functions in the state.
      setCreateUnSubs(
        subscriptions.map(subscription =>
          queryResult.subscribeToMore({
            document: subscription.query,
            variables: subscription.variables,
            // When subscription data is pushed from Graphql server, update original query result
            updateQuery: getQueryUpdaterForSubscription(subscription, selectEntityId, selectEntities),
          })
        )
      );
    }
  }, [createUnSubs, subscriptions, queryResult, selectEntities, selectEntityId]);

  // Call the unsubscribe callbacks for all the subscriptions on unmount.
  React.useEffect(
    () => () => {
      createUnSubs?.forEach(unsub => unsub());
    },
    [createUnSubs]
  );

  return { subscribe };
}

function getQueryUpdaterForSubscription<QueryResponse, EntityResponse>(
  subscription: SubscriptionDescriptor,
  selectEntityId: (entity: EntityResponse) => string,
  selectEntities: (resp?: QueryResponse) => EntityResponse[]
): UpdateQueryFn<QueryResponse> {
  return (prevState: QueryResponse, { subscriptionData }) => {
    if (!subscriptionData.data || !prevState) {
      return prevState;
    }

    const newEntity = subscriptionData.data[subscription.name ?? DEFAULT_CREATE_SUBSCRIPTION_NAME] as EntityResponse;

    if (selectEntities(prevState).find(e => selectEntityId(e) === selectEntityId(newEntity))) {
      return prevState;
    }

    return produce(prevState, draftState => {
      // collects entities from previous query response from Apollo cache and adds created one from subscription on top of it
      const entities = selectEntities(draftState as QueryResponse);

      entities.unshift(newEntity);
    });
  };
}
